import * as React from 'react';

import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { faUserPen } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth';
import { commonMessages } from 'src/features/auth/utils';
import {
    type UpdateUserFormSchemaValues,
    updateUserFormSchema,
    useUpdateUser,
} from 'src/features/profile/api/useUpdateUser';
import { detailsMessages } from 'src/features/profile/utils';
import useHasPermissions from 'src/hooks/useHasPermissions';
import {
    FormFieldBlock,
    FormFieldBlockLabel,
    FormFieldBlockTitle,
    FormFieldBlockName,
    FormFieldBlockSubtitle,
    FormFieldBlockBody,
} from 'src/registry/components/blocks/FormFieldBlock';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { useToast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { Form, FormControl, FormField, FormItem, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { generalErrorMessages, generalLabelMessages, generalMessages } from 'src/utils/messages';

import type { User } from 'src/features/auth';

type ProfileInfoFormProps = {
    userId: string;
    data: User;
    refetch: () => void;
};

const ProfileInfoForm = ({ data, userId, refetch }: ProfileInfoFormProps) => {
    const { formatMessage } = useIntl();
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = React.useState(false);
    const permissionManage = useHasPermissions([PERMISSION.PROFILE_MANAGE]);
    const { toast } = useToast();

    const form = useForm<UpdateUserFormSchemaValues>({
        resolver: zodResolver(updateUserFormSchema),
        mode: 'onChange',
        defaultValues: {
            first_name: data.first_name,
            last_name: data.last_name,
        },
    });

    const { mutate, isPending } = useUpdateUser({
        onSuccess: ({ first_name, last_name, name }) => {
            form.reset({ first_name, last_name });
            refetch();
            setIsConfirmDialogOpen(false);
            toast({
                title: formatMessage(detailsMessages.nameChangeSuccess),
                description: formatMessage(detailsMessages.nameChangeSuccessDetails, { name }),
            });
        },
        onError: (e) => {
            if (e?.response?.data?.errors) {
                toast({
                    variant: 'destructive',
                    title: formatMessage(generalErrorMessages.somethingWentWrong),
                    description: `${formatMessage(generalLabelMessages.details)}: ${e?.response?.data?.errors[0]?.detail}`,
                });
            }
        },
    });

    // See src/features/profile/components/ProfileSettingsForm.tsx for explanation
    React.useEffect(() => {
        void form.trigger();
    }, []);

    const onSubmit = (values: UpdateUserFormSchemaValues) => {
        mutate({ userId, ...values });
    };

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset();
        }
        setIsConfirmDialogOpen(isOpen);
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <Dialog open={isConfirmDialogOpen} onOpenChange={onOpenChange}>
                <DialogContent>
                    <DialogHeader className="flex-row gap-4">
                        <div className="flex gap-4">
                            <SquareIcon icon={faUserPen} />
                            <div className="grid text-sm">
                                <DialogTitle>{formatMessage(detailsMessages.nameChange)}</DialogTitle>
                                <DialogDescription>
                                    {formatMessage(detailsMessages.nameChangeSubtitle)}
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>

                    <DialogSeparator />

                    <DialogBody>
                        <p className="text-gray-500">
                            {formatMessage(detailsMessages.nameChangeDialog, {
                                name: form.getValues().first_name + ' ' + form.getValues().last_name,
                            })}
                        </p>
                    </DialogBody>

                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="outline" type="button" className="flex-1">
                                {formatMessage(generalMessages.cancel)}
                            </Button>
                        </DialogClose>

                        <Button
                            type="submit"
                            className="flex-1"
                            onClick={form.handleSubmit(onSubmit)}
                            disabled={!form.formState.isDirty || !form.formState.isValid}
                        >
                            {formatMessage(generalMessages.saveChanges)}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(detailsMessages.name)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                    <FormFieldBlockSubtitle>{formatMessage(detailsMessages.nameSubtitle)}</FormFieldBlockSubtitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody>
                    <FormField
                        control={form.control}
                        name="first_name"
                        render={({ field }) => (
                            <FormItem className="w-full">
                                <FormControl>
                                    <Input
                                        type="text"
                                        placeholder={formatMessage(commonMessages.firstNamePlaceholder)}
                                        disabled={!permissionManage}
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="last_name"
                        render={({ field }) => (
                            <FormItem className="w-full">
                                <FormControl>
                                    <Input
                                        type="text"
                                        placeholder={formatMessage(commonMessages.lastNamePlaceholder)}
                                        disabled={!permissionManage}
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>

            {/* <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(detailsMessages.picture)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                    <FormFieldBlockSubtitle>{formatMessage(detailsMessages.pictureSubtitle)}</FormFieldBlockSubtitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody>
                    <ProfilePictureInput />
                </FormFieldBlockBody>
            </FormFieldBlock>

            <FormFieldBlockSeparator /> */}

            {permissionManage && (
                <Button
                    className="sm:place-self-end"
                    type="button"
                    onClick={() => setIsConfirmDialogOpen(true)}
                    disabled={!form.formState.isDirty || !form.formState.isValid || isPending}
                >
                    {isPending && <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin text-lg" />}
                    {formatMessage(generalMessages.saveChanges)}
                </Button>
            )}
        </Form>
    );
};

export default ProfileInfoForm;
